import { useEffect, useState } from "react";

export const THEME_STORAGE_KEY = "akh-dashboard-theme";
const THEME_CHANGE_EVENT = "akh-theme-change";

/**
 * Resolve theme: saved preference, else prefers-color-scheme.
 */
export function getPreferredTheme() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === "light" || saved === "dark") {
      return saved;
    }
  } catch (e) {
    // ignore (private mode / blocked storage)
  }
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

/**
 * Apply theme to <html data-theme> and persist.
 */
export function applyTheme(theme) {
  const next = theme === "dark" ? "dark" : "light";
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", next);
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch (e) {
    // ignore
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(THEME_CHANGE_EVENT, { detail: next })
    );
  }
  return next;
}

/**
 * Icon button that toggles light/dark theme.
 * Mount in AdminNavbar (desktop) and Sidebar mobile nav.
 */
const ThemeToggle = ({ className = "" }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof document !== "undefined") {
      const current = document.documentElement.getAttribute("data-theme");
      if (current === "light" || current === "dark") {
        return current;
      }
    }
    return getPreferredTheme();
  });

  useEffect(() => {
    const onThemeChange = (event) => {
      const next = event.detail;
      if (next === "light" || next === "dark") {
        setTheme(next);
      }
    };
    window.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
  }, []);

  const isDark = theme === "dark";
  const nextLabel = isDark ? "Switch to light theme" : "Switch to dark theme";
  const visibleLabel = isDark ? "Light" : "Dark";

  const handleToggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    applyTheme(next);
    setTheme(next);
  };

  return (
    <button
      type="button"
      className={`akh-theme-toggle ${className}`.trim()}
      onClick={handleToggle}
      aria-label={nextLabel}
      title={nextLabel}
      aria-pressed={isDark}
    >
      <i className={isDark ? "fas fa-sun" : "fas fa-moon"} aria-hidden="true" />
      <span className="akh-theme-toggle__label">{visibleLabel}</span>
    </button>
  );
};

export default ThemeToggle;
